import { api } from "../../../../api"
import { setAlertFunc } from "../../../../components/AlertToaster/AlertSlice"
import {
    HistoryData,
    IResponse,
    ProjectCategory,
    StatusButton,
    TicketDataResponseType,
} from "../../../Common/commonTypes"

type AddressObj = {
    access_type: string
    latitude: number
    longitude: number
    ops_ticket_id: number
    pincode: number
    project_id: number
    project_name: string
    property_type: string
    site_id: string
}
type GetSkipStatusesParams = {
    search_key?: string
    ticket_id: string
    sub_module: string
}
const MODULE_URL = "operations"
const EV_MODULE_URL = "operations/ev_applications"
const EV_MODULE_V3 = "ev_applications"

export interface IEvAppProfileSiteDetail {
    site_id: number
    site_id_str: string
    site_name: string
    address: string
    city: string
    state: string
    pincode: string
    country: string
    country_id: number
    latitude: number | null
    longitude: number | null
    map_link: string | null
}

export interface IEvAppConnection {
    application_date: string | null
    application_number: string | null
    applied_load: string | null
    connection_account_number: string | null
    connection_owned_by: string | null
    connection_status: string | null
    date_of_energization: string | null
    discom_name: string | null
    was_substation_installed: number | null
    refundable_amount: number | string | null
    non_refundable_amount: number | string | null
}

export interface IEvAppMeterDetails {
    images: any
    sbm_installed_by: string | null
    sbm_number: string | null
    sbm_owner: string | null
    sbm_start_reading: string | null
}

export interface IEvAppPaymentDetail {
    amount_paid: string | null
    approval_name_L1: string | null
    approval_name_L2: string | null
    approval_status_L1: string | null
    approval_status_L2: string | null
    department_name: string | null
    discom_name: string | null
    expense_type: string | null
    gst_amount: string | null
    invoice_date: string | null
    invoice_number: string | null
    item_category: string | null
    module_id: number | null
    non_refundable_amount: string | null
    payment_date: string | null
    payment_status: string | null
    raised_by_name: string | null
    refundable_amount: string | null
    requested_amount: string | null
    status: string | null
    total_amount: string | null
    txn_created_at: string | null
    txn_id: number | null
    utr_number: string | null
}

export interface IEvAppConnectionDetailsResponse {
    connection: IEvAppConnection
    meter_details: IEvAppMeterDetails
    payment_details: IEvAppPaymentDetail[]
    show_alert: boolean
}

export interface IEvAppProfileOverviewResponse {
    project_id: number
    data: {
        common: {
            project: {
                project_id: number
                project_name: string
                display_id: string
                created_at: string
                deal_type: string
                template_id: number
                template_name: string
            }
            site_details: {
                source: IEvAppProfileSiteDetail
                destination: IEvAppProfileSiteDetail
            }
        }
        overview: {
            project_id: number
            project_name: string
            display_id: string
            project_details: {
                template_id: number
                template_name: string
                deal_type: string
                category: ProjectCategory
                created_at: string
                created_by: { id: number; name: string; email: string }
                program: { name?: string; program_name?: string; program_id?: number } | null
            }
            destination_address: IEvAppProfileSiteDetail | null
        }
    }
}

const EVApplicationService = api.injectEndpoints({
    endpoints: build => ({
        getEVTabs: build.query({
            query: params => ({
                url: `${EV_MODULE_V3}/total`,
                useV3: true,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    blocked_reason_id: JSON.stringify(params.data.blocked_reason_id ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    site_id: JSON.stringify(params.data.siteIds ?? []),
                    ev_ticket_type: params.data.ticketType ?? "",
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    discom_name: params.data?.discom_name ?? "",
                },
            }),
            providesTags: ["EvApplication"],
        }),
        getEVData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${EV_MODULE_V3}/${params.data.parent_status ?? "all_tab"}`,
                useV3: true,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    blocked_reason_id: JSON.stringify(params.data.blocked_reason_id ?? []),
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    site_id: JSON.stringify(params.data.siteIds ?? []),
                    ev_ticket_type: params.data.ticketType ?? "",
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    ...(params.data.download && { download: true }),
                    discom_name: params.data?.discom_name ?? "",
                },
            }),
            providesTags: ["EvApplication"],
        }),
        getRaisedByUsers: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/raised_by_users`,
            }),
            providesTags: ["EvApplication"],
        }),
        getEvAutofillData: build.query<AddressObj, { id: number | string }>({
            query: ({ id }) => ({
                url: `ev_solo_fill`,
                params: {
                    ops_ticket_id: id ?? "",
                },
            }),
            transformResponse: (response: IResponse<any>) => response.data,
        }),
        getHoistingAddress: build.query<IResponse<any>, { state_id: number; city_id: number }>({
            query: obj => ({
                url: `hosting_address`,
                params: {
                    state_id: obj?.state_id ?? "",
                    city_id: obj?.city_id ?? "",
                },
            }),
        }),
        getEvApplicationDetails: build.query<IResponse<any>, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/ticket`,
                useV3: true,
                params: {
                    ticket_id: ticketId ?? "",
                },
            }),
            providesTags: (result, error, arg) => ["EvApplication", { type: "EvApplication", id: arg.ticketId }],
        }),
        getEvApplicationProfileOverview: build.query<IEvAppProfileOverviewResponse, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/project_profile_overview`,
                useV3: true,
                params: {
                    ticket_id: ticketId ?? "",
                },
            }),
            providesTags: ["EvApplication", "Projects"],
        }),
        getEvApplicationConnectionDetails: build.query<IEvAppConnectionDetailsResponse, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/connection_details`,
                useV3: true,
                params: {
                    ticket_id: ticketId ?? "",
                },
            }),
            transformResponse: (response: IResponse<IEvAppConnectionDetailsResponse>) => response.data,
            providesTags: ["EvApplication"],
        }),
        getEvApplicationSalesDetails: build.query<any, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/sales_details`,
                method: "GET",
                useV3: true,
                params: {
                    ticket_id: ticketId ?? "",
                },
            }),
            providesTags: ["EvApplication", "Projects", "Finance"],
        }),
        getMeterDetails: build.query<any, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/meter_details`,
                useV3: true,
                params: {
                    ticket_id: ticketId ?? "",
                },
            }),
        }),
        getLinkProject: build.query<any, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `${EV_MODULE_V3}/link`,
                useV3: true,
            }),
        }),
        getOpsSkipStatuses: build.query<IResponse<any>, GetSkipStatusesParams>({
            query: obj => ({
                url: `${MODULE_URL}/statuses`,
                params: {
                    search_key: obj.search_key ?? "",
                    ticket_id: obj.ticket_id ?? "",
                    sub_module: obj.sub_module ?? "",
                },
            }),
        }),
        getEvButtonDropdown: build.query<StatusButton[], { ticket_id: string }>({
            query: params => ({
                url: `${EV_MODULE_V3}/button_dropdown`,
                useV3: true,
                params,
            }),
            providesTags: ["EvApplication"],
        }),
        // Mutations
        addEvInfraTicket: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${EV_MODULE_URL}/ticket`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Ticket Created!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong" }))
                }
            },
            invalidatesTags: ["EvApplication"],
        }),
        addEVTicket: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ev_application`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Ticket Created!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["EvApplication"],
        }),
        editEvSurvey: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `${EV_MODULE_V3}/ticket`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["EvApplication"],
        }),
        editMeterDetails: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${EV_MODULE_V3}/meter_details`,
                method: "POST",
                useV3: true,
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["EvApplication"],
        }),
        editEvLinkProject: build.mutation({
            query: body => ({
                url: `${EV_MODULE_V3}/link`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["EvApplication"],
        }),
        getEVApplicationHistory: build.query<HistoryData[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${EV_MODULE_V3}/history`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: (result, error, arg) => ["EvApplication"],
        }),
        OpsEditSkipStatus: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: data.message !== "Access denied" ? "success" : "error",
                            msg: data?.message,
                        })
                    )
                } catch (error) {
                    dispatch(setAlertFunc({ type: "error", msg: "Failed to fetch ticket details" }))
                }
            },
            invalidatesTags: ["EvApplication", "Installation"],
        }),
        editEvApplicationReadUnreadStatus: build.mutation({
            query: body => ({
                url: `${EV_MODULE_V3}/mark_read`,
                useV3: true,
                method: "POST",
                body,
            }),
            invalidatesTags: ["EvApplication"],
        }),
        editEvApplicationConnectionDetails: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                discom_name?: string | null
                connection_owned_by?: string | null
                connection_account_number?: string | null
                application_number?: string | null
                application_date?: string | null
                applied_load?: string | null
                date_of_energization?: string | null
                was_substation_installed?: 0 | 1 | null
            }
        >({
            query: ({ ticket_id, ...body }) => ({
                url: `${EV_MODULE_V3}/${ticket_id}/connection_details`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["EvApplication"],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.message || "Connection details updated successfully",
                        })
                    )
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            msg: error?.error?.data?.message || error?.message || "Failed to update connection details",
                        })
                    )
                }
            },
        }),
    }),
})

export const {
    useLazyGetEVTabsQuery,
    useLazyGetEVDataQuery,
    useGetRaisedByUsersQuery,
    useAddEVTicketMutation,
    useEditEvSurveyMutation,
    useGetLinkProjectQuery,
    useOpsEditSkipStatusMutation,
    useLazyGetHoistingAddressQuery,
    useGetEvApplicationDetailsQuery,
    useGetEvApplicationProfileOverviewQuery,
    useGetEvApplicationConnectionDetailsQuery,
    useGetEvApplicationSalesDetailsQuery,
    useGetEVApplicationHistoryQuery,
    useLazyGetEvAutofillDataQuery,
    useGetEvButtonDropdownQuery,
    useGetMeterDetailsQuery,
    useLazyGetMeterDetailsQuery,
    useEditMeterDetailsMutation,
    useGetOpsSkipStatusesQuery,
    useEditEvLinkProjectMutation,
    useAddEvInfraTicketMutation,
    useEditEvApplicationReadUnreadStatusMutation,
    useEditEvApplicationConnectionDetailsMutation,
} = EVApplicationService

export { EVApplicationService }
